/**
 * Platform API Bridge
 *
 * Client-side API for platform information.
 * Communicates with Nest when running inside Electron.
 * Provides cross-platform path handling utilities.
 */

import { isNestApiAvailable, requestNestMethod } from "../nest/client"

// ============================================================================
// Type Definitions
// Nest descriptor-generated types are the contract for migrated call sites.
// ============================================================================

export interface PlatformInfo {
    platform: "win32" | "darwin" | "linux"
    pathSeparator: "/" | "\\"
    homeDir: string
    isWindows: boolean
    isMac: boolean
    isLinux: boolean
}

// ============================================================================
// Cached Platform Info
// ============================================================================

let cachedPlatformInfo: PlatformInfo | null = null

export function resetPlatformInfo(): void {
    cachedPlatformInfo = null
}

/**
 * Default platform info for non-Electron environments (browser)
 * Assumes Unix-like environment for web fallback
 */
const defaultPlatformInfo: PlatformInfo = {
    platform: "darwin",
    pathSeparator: "/",
    homeDir: "/",
    isWindows: false,
    isMac: true,
    isLinux: false,
}

function normalizeNestPlatformInfo(value: unknown): PlatformInfo | null {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return null
    const info = value as Record<string, unknown>
    if (info.platform !== "win32" && info.platform !== "darwin" && info.platform !== "linux") return null
    const pathSeparator = info.pathSeparator === "\\" ? "\\" : "/"
    const homeDir = typeof info.homeDir === "string" ? info.homeDir : "/"
    return {
        platform: info.platform,
        pathSeparator,
        homeDir,
        isWindows: info.platform === "win32",
        isMac: info.platform === "darwin",
        isLinux: info.platform === "linux",
    }
}

// ============================================================================
// Platform API Functions
// ============================================================================

/**
 * Fetch platform info from Nest.
 * Caches the result for subsequent calls
 */
export async function fetchPlatformInfo(): Promise<PlatformInfo> {
    if (cachedPlatformInfo) return cachedPlatformInfo

    if (!isNestApiAvailable()) {
        console.warn("[PlatformAPI] Nest is unavailable, using default platform info")
        cachedPlatformInfo = defaultPlatformInfo
        return cachedPlatformInfo
    }

    const response = await requestNestMethod("host.platform.read")
    const normalized = normalizeNestPlatformInfo(response)
    if (!normalized) {
        throw new Error("Nest returned invalid platform info.")
    }
    cachedPlatformInfo = normalized
    return normalized
}

/**
 * Get cached platform info synchronously
 * Returns default info if not yet fetched - call fetchPlatformInfo() first
 */
export function getPlatformInfo(): PlatformInfo {
    return cachedPlatformInfo ?? defaultPlatformInfo
}

/**
 * Get the OS-specific file manager name (Finder, Explorer, File Manager)
 */
export function getFileManagerName(): string {
    const { platform } = getPlatformInfo()
    if (platform === "darwin") return "Finder"
    if (platform === "win32") return "Explorer"
    return "File Manager"
}

/**
 * Get the platform's native path separator
 * Returns "/" on Unix/Mac, "\\" on Windows
 */
export function getPathSeparator(): "/" | "\\" {
    return getPlatformInfo().pathSeparator
}
